"use client";

import { useEffect, useState, useRef } from "react";
import dynamic from "next/dynamic";

// Dynamic import of react-pdf components to prevent SSR issues
const DynamicPdfViewer = dynamic(
  async () => {
    // Import react-pdf only on client
    const { Document, Page, pdfjs } = await import("react-pdf");
    pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";

    // Return a component that uses the loaded modules
    return function PdfViewer({ pdfBlob }: { pdfBlob: Blob | null }) {
      const [numPages, setNumPages] = useState<number>(0);
      const [pageNumber, setPageNumber] = useState<number>(1);
      const [scale, setScale] = useState<number>(1);
      const containerRef = useRef<HTMLDivElement>(null);

      // Calculate optimal scale to fit PDF within container (no scroll)
      useEffect(() => {
        const updateScale = () => {
          if (!containerRef.current) return;
          const rect = containerRef.current.getBoundingClientRect();

          const availableWidth = rect.width - 32; // 16px padding each side
          // Leave space for navigation controls (approx 60px)
          const availableHeight = window.innerHeight - 250;

          // A4 PDF dimensions at scale 1.0 (points)
          const pdfWidth = 595;
          const pdfHeight = 842;

          // Fit PDF to container width and height
          const scaleWidth = availableWidth / pdfWidth;
          const scaleHeight = availableHeight / pdfHeight;
          
          // Use the smaller scale to fit within both dimensions
          const fitScale = Math.min(scaleWidth, scaleHeight);

          setScale(Math.max(fitScale, 0.4)); // Min scale 0.4x
        };

        updateScale();
        window.addEventListener("resize", updateScale);
        return () => window.removeEventListener("resize", updateScale);
      }, []);

      const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
        setNumPages(numPages);
        setPageNumber(1);
      };

      const goToPrevPage = () => {
        setPageNumber((prev) => Math.max(prev - 1, 1));
      };

      const goToNextPage = () => {
        setPageNumber((prev) => Math.min(prev + 1, numPages));
      };

      if (!pdfBlob) return null;

      return (
        <div
          ref={containerRef}
          className="flex h-full flex-col items-center justify-center overflow-hidden"
          dir="rtl"
        >
          <Document
            file={pdfBlob}
            onLoadSuccess={onDocumentLoadSuccess}
            loading={null}
            error={null}
            className="flex flex-col items-center justify-center"
          >
            <Page
              pageNumber={pageNumber}
              scale={scale}
              renderTextLayer={false}
              renderAnnotationLayer={false}
              className="shadow-lg"
            />
          </Document>

          {/* Navigation Controls */}
          {numPages > 1 && (
            <div className="mt-4 flex items-center gap-4">
              <button
                onClick={goToPrevPage}
                disabled={pageNumber <= 1}
                className="rounded-lg bg-neutral-900 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-neutral-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                הקודם
              </button>
              <span className="text-sm text-neutral-600">
                עמוד {pageNumber} מתוך {numPages}
              </span>
              <button
                onClick={goToNextPage}
                disabled={pageNumber >= numPages}
                className="rounded-lg bg-neutral-900 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-neutral-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                הבא
              </button>
            </div>
          )}
        </div>
      );
    };
  },
  { ssr: false, loading: () => <div className="h-full w-full" /> },
);

export default function LegalPage() {
  const [termsPdfBlob, setTermsPdfBlob] = useState<Blob | null>(null);
  const [privacyPdfBlob, setPrivacyPdfBlob] = useState<Blob | null>(null);

  useEffect(() => {
    // Load Terms of Service PDF
    fetch("/terms-of-service.pdf")
      .then((response) => response.blob())
      .then((blob) => setTermsPdfBlob(blob))
      .catch((error) => console.error("Failed to load terms PDF:", error));

    // Load Privacy Policy PDF
    fetch("/privacy-policy.pdf")
      .then((response) => response.blob())
      .then((blob) => setPrivacyPdfBlob(blob))
      .catch((error) => console.error("Failed to load privacy PDF:", error));
  }, []);

  return (
    <div className="h-[calc(100vh-64px)] w-full overflow-hidden bg-white pb-20 lg:h-screen lg:pb-0 lg:ps-16">
      <div className="mx-auto h-full max-w-7xl px-4 py-4" dir="rtl">
        {/* PDFs Side by Side */}
        <div className="grid h-full gap-4 lg:grid-cols-2">
          {/* Terms of Service Section */}
          <section className="flex h-full flex-col overflow-hidden">
            <h2 className="mb-2 text-center text-xl font-semibold text-neutral-900">
              תנאי שימוש
            </h2>
            <div className="flex-1 overflow-hidden">
              <DynamicPdfViewer pdfBlob={termsPdfBlob} />
            </div>
          </section>

          {/* Privacy Policy Section */}
          <section className="flex h-full flex-col overflow-hidden">
            <h2 className="mb-2 text-center text-xl font-semibold text-neutral-900">
              מדיניות פרטיות
            </h2>
            <div className="flex-1 overflow-hidden">
              <DynamicPdfViewer pdfBlob={privacyPdfBlob} />
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
